import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      nav("/");
    } catch (err) {
      toast.error("Registration failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-100 p-6">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-8">
        <div className="flex items-center gap-2 mb-8">
          <Building2 className="text-orange-600 h-6 w-6" />
          <div className="font-heading font-black tracking-tight">MASCOT — Register</div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="overline">Full name</Label>
            <Input
              data-testid="register-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="rounded-sm mt-1.5 border-zinc-300"
            />
          </div>
          <div>
            <Label className="overline">Email</Label>
            <Input
              data-testid="register-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="rounded-sm mt-1.5 border-zinc-300"
            />
          </div>
          <div>
            <Label className="overline">Password</Label>
            <Input
              data-testid="register-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              className="rounded-sm mt-1.5 border-zinc-300"
            />
          </div>
          <div>
            <Label className="overline">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="register-role-select" className="rounded-sm mt-1.5 border-zinc-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-orange-600 hover:bg-orange-700 rounded-sm h-11"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
          </Button>
        </form>
        <div className="mt-6 text-xs text-zinc-500 text-center">
          Have an account?{" "}
          <Link to="/login" className="text-orange-600 font-medium hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
